import React, { useState, useEffect, useRef, useMemo } from "react";
import { PRIOS } from "../constants";
import { fetchCategories } from "../api";
import PrioIcon from "./PrioIcon";
import ConfirmDialog from "./ConfirmDialog";
import DatePicker from "./DatePicker";
import CustomSelect from "./CustomSelect";
import "./TaskForm.css";

export default function TaskForm({ task, employees, areas, onSubmit, onCancel, onDelete, onDirtyChange }) {
  const isEdit = !!task?.id;

  const [form, setForm] = useState({
    name: task?.name || "",
    person_ids: task?.person_ids || [],
    area: task?.area || "intern",
    category: task?.category || "",
    deadline: task?.deadline || "",
    prio: task?.prio || "medium",
    calendar_date: task?.calendar_date || "",
    notes: task?.notes || "",
  });

  // Report whether the form differs from its initial values — the parent uses
  // this to guard the backdrop-close against silently discarding input.
  const dirtySnapshotRef = useRef(null);
  useEffect(() => {
    if (!onDirtyChange) return;
    const snap = JSON.stringify(form);
    if (dirtySnapshotRef.current === null) { dirtySnapshotRef.current = snap; return; }
    onDirtyChange(snap !== dirtySnapshotRef.current);
  }, [form, onDirtyChange]);
  useEffect(() => () => { if (onDirtyChange) onDirtyChange(false); }, [onDirtyChange]);

  const [error, setError] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [categories, setCategories] = useState([]);
  const [catOpen, setCatOpen] = useState(false);
  const catRef = useRef(null);

  useEffect(() => {
    fetchCategories(form.area).then(setCategories).catch(() => {});
  }, [form.area]);

  useEffect(() => {
    const handleClick = (e) => { if (catRef.current && !catRef.current.contains(e.target)) setCatOpen(false); };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const catFiltered = categories.filter(
    (k) => k.toLowerCase().includes((form.category || "").toLowerCase()) && k !== form.category
  );

  const employeeList = useMemo(() => employees || [], [employees]);

  // Only active staff is offered. Archived people already on this task keep their
  // chip (captured once, so de-selecting stays undoable) — the assignment must
  // not silently disappear just because they were archived.
  const initialPersonIds = useRef(task?.person_ids || []);
  const personChips = useMemo(() => [
    ...employeeList.filter((m) => !m.archived),
    ...employeeList.filter((m) => m.archived && initialPersonIds.current.includes(m.id)),
  ], [employeeList]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const togglePerson = (id) => {
    const ids = form.person_ids || [];
    setForm({
      ...form,
      person_ids: ids.includes(id) ? ids.filter((pid) => pid !== id) : [...ids, id],
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");

    if (!form.name) {
      setError("Bitte alle Pflichtfelder ausfüllen.");
      return;
    }

    const data = { ...form };
    if (!data.category) data.category = null;
    if (!data.calendar_date) data.calendar_date = null;
    if (!data.deadline) data.deadline = null;

    onSubmit(data);
  };

  const renderPrioOption = (opt) => (
    <span className="prio-option-label">
      <PrioIcon prio={opt.value} size={14} />
      {opt.label}
    </span>
  );

  return (
    <form className="task-form" onSubmit={handleSubmit}>
      <div className="modal-header">
        <h2 className="modal-title">{isEdit ? "Task bearbeiten" : "Neuer Task"}</h2>
        <button type="button" className="modal-close-btn" onClick={onCancel} title="Schließen">&times;</button>
      </div>

      {error && <div className="form-error">{error}</div>}

      <div className="form-row">
        <label className="task-form-label">Name</label>
        <input name="name" value={form.name} onChange={handleChange} placeholder="Task-Bezeichnung" />
      </div>

      <div className="form-row-half">
        <div className="form-row">
          <label className="task-form-label">Bereich</label>
          <CustomSelect
            value={form.area}
            options={(areas || []).map((k) => ({ value: k.key, label: k.label }))}
            onChange={(v) => setForm({ ...form, area: v })}
          />
        </div>

        <div className="form-row">
          <label className="task-form-label">
            Person
            <div className="task-person-chips">
              {personChips.map((m) => {
                const selected = (form.person_ids || []).includes(m.id);
                return (
                  <button
                    key={m.id}
                    type="button"
                    className={`task-person-chip ${selected ? "task-person-chip-active" : ""}`}
                    style={selected ? { background: m.color, borderColor: m.color } : {}}
                    title={m.archived ? `${m.name} (archiviert)` : m.name}
                    onClick={() => togglePerson(m.id)}
                  >
                    {m.abbreviation || m.name}
                  </button>
                );
              })}
            </div>
          </label>
        </div>
      </div>

      <button
        type="button"
        className="form-advanced-toggle"
        onClick={() => setShowAdvanced(!showAdvanced)}
      >
        <svg className={`form-advanced-chevron ${showAdvanced ? "" : "collapsed"}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
        erweiterte Angaben
      </button>

      {showAdvanced && (
        <>
          <div className="form-row-half">
            <div className="form-row">
              <label className="task-form-label">Priorität</label>
              <CustomSelect
                value={form.prio}
                options={PRIOS.map((p) => ({ value: p.value, label: p.label }))}
                onChange={(v) => setForm({ ...form, prio: v })}
                renderOption={renderPrioOption}
              />
            </div>

            <div className="form-row task-cat-row" ref={catRef}>
              <label className="task-form-label">Kategorie</label>
              <div className="task-cat-wrap">
                <input
                  value={form.category || ""}
                  onChange={(e) => { setForm({ ...form, category: e.target.value }); setCatOpen(true); }}
                  onFocus={() => setCatOpen(true)}
                  placeholder="Buchhaltung"
                  autoComplete="off"
                />
                {catOpen && catFiltered.length > 0 && (
                  <div className="cat-suggestions">
                    {catFiltered.map((k) => (
                      <div key={k} className="cat-suggestion-item" onMouseDown={() => { setForm({ ...form, category: k }); setCatOpen(false); }}>
                        {k}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="form-row-half">
            <div className="form-row">
              <label className="task-form-label">Deadline</label>
              <DatePicker value={form.deadline} onChange={(v) => setForm({ ...form, deadline: v })} />
            </div>

            <div className="form-row">
              <label className="task-form-label">Kalender</label>
              <DatePicker value={form.calendar_date} onChange={(v) => setForm({ ...form, calendar_date: v })} />
            </div>
          </div>

          <div className="form-row">
            <label className="task-form-label">Notizen</label>
            <textarea
              name="notes"
              value={form.notes}
              onChange={handleChange}
              placeholder="Notizen, Listen, Todos..."
              className="form-notes"
              rows={4}
            />
          </div>
        </>
      )}

      <div className="form-actions">
        {isEdit && (
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={() => setShowDeleteConfirm(true)}
          >Löschen</button>
        )}
        <div className="form-actions-right">
          <button type="submit" className="btn btn-primary">{isEdit ? "Speichern" : "Erstellen"}</button>
        </div>
      </div>
      {showDeleteConfirm && (
        <ConfirmDialog
          message={`Task "${task.name}" wirklich löschen?`}
          onConfirm={() => onDelete(task.id)}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}
    </form>
  );
}
